/**
 * `canary record` — Record browser interactions for flow creation.
 *
 * Launches a headed browser, injects an in-page capture script,
 * and records user interactions (clicks, inputs, navigation) enriched
 * with accessibility snapshots and element info. On stop, bundles
 * everything as JSONL + video and uploads to the API.
 *
 * @module record
 */

import fs from 'node:fs/promises';
import path from 'node:path';
import { getCanaryTmpDir } from '@chatsdet/tmp';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, selectCredential, downloadStorageState } from './cli-helpers.js';

import { INTERACTION_CAPTURE_SCRIPT } from './record-interaction-script.js';
import type { CapturedInteractionEvent } from './session/types.js';

/** Recording event — uses the shared CapturedInteractionEvent type. */
type RecordingEvent = CapturedInteractionEvent;

/** Click event narrowing helper for enrichment. */
type RecordingClickEvent = Extract<CapturedInteractionEvent, { type: 'click' }>;

interface CredentialDetail {
  id: string;
  name: string;
  loginUrl?: string;
  storageStateS3Key?: string | null;
}

export async function runRecord(argv: string[]): Promise<void> {
  if (hasFlag(argv, '--help', '-h')) {
    console.log(
      [
        'Usage: canary record [options]',
        '',
        'Record browser interactions for flow creation.',
        '',
        'Options:',
        '  --credential <id>  Credential ID (skip interactive selection)',
        '  --url <startUrl>   URL to navigate to after launch',
        '  --output <dir>     Local output directory (default: temp dir)',
        '  --env <env>        Environment (local, dev, prod)',
        '  --no-upload        Save locally only, skip API upload',
        '',
        'Press Ctrl+C to stop recording.',
      ].join('\n')
    );
    return;
  }

  const config = await resolveConfig(argv);
  const credentialArg = getArgValue(argv, '--credential');
  const startUrl = getArgValue(argv, '--url');
  const outputDir = getArgValue(argv, '--output');
  const skipUpload = hasFlag(argv, '--no-upload');

  // 1. Fetch and select credential
  console.log('Fetching credentials...');
  const credential = await selectCredential(config.apiUrl, config.token, credentialArg);
  if (!credential) {
    process.exit(1);
  }

  console.log(`Using credential: ${credential.name}`);

  const propertyId = credential.propertyId;

  // 3. Download storage state if available
  let storageStatePath: string | undefined;
  if (credential.storageStateS3Key) {
    console.log('Downloading storage state...');
    storageStatePath = await downloadStorageState({
      apiUrl: config.apiUrl,
      token: config.token,
      propertyId: credential.propertyId,
      credentialId: credential.id,
    });
    if (storageStatePath) {
      console.log('Storage state loaded.');
    } else {
      console.warn('Could not download storage state, continuing without it.');
    }
  }

  // 4. Get credential detail — its loginUrl is the resolved login start URL
  // (environment URL + the application's relative login path).
  const detail = await apiRequest<{ ok: boolean; credential?: CredentialDetail }>(
    config.apiUrl,
    config.token,
    'GET',
    `/org/properties/${propertyId}/credentials/${credential.id}`
  );
  const loginUrl = detail.credential?.loginUrl ?? undefined;

  // 5. Determine start URL
  const navigateUrl = startUrl ?? loginUrl;
  if (!navigateUrl) {
    console.warn(
      'No start URL provided and the application has no environment URL. Browser will open to about:blank.'
    );
  }

  // 6. Launch browser
  console.log('Launching browser...');

  // Lazy-load playwright-dependent modules
  const { PlaywrightClient, consoleLogger, captureElementAtPoint } =
    await import('@chatsdet/browser-core');

  const client = new PlaywrightClient({ logger: consoleLogger });
  await client.connect({
    browserMode: 'headed',
    storageStatePath,
    recordVideo: true,
  });

  const page = await client.getPageForReplay();
  if (!page) {
    console.error('Failed to get browser page.');
    await client.disconnect();
    process.exit(1);
  }

  // 7. Inject interaction capture script
  await page.addInitScript(INTERACTION_CAPTURE_SCRIPT);
  await page.evaluate(INTERACTION_CAPTURE_SCRIPT);

  // 8. Navigate
  if (navigateUrl) {
    console.log(`Navigating to ${navigateUrl}`);
    await page.goto(navigateUrl, { waitUntil: 'domcontentloaded', timeout: 30_000 }).catch(() => {
      console.warn('Navigation timed out or failed, continuing anyway.');
    });
  }

  const events: RecordingEvent[] = [];
  let running = true;

  // Emit initial-url event so we know the starting page
  const currentUrl = page.url();
  const initialUrlEvent: RecordingEvent = {
    type: 'initial-url',
    ts: Date.now(),
    url: currentUrl,
  };
  try {
    const snapshot = await page.ariaSnapshot({ mode: 'ai' });
    (initialUrlEvent as { snapshot?: string }).snapshot = snapshot;
  } catch {
    // Snapshot may fail
  }
  events.push(initialUrlEvent);
  process.stdout.write(`  [${events.length}] initial-url → ${currentUrl}\n`);

  console.log('\nRecording started. Interact with the browser.');
  console.log('Press Ctrl+C to stop recording.\n');

  // 9. Poll loop — collect events from the page
  const pollInterval = setInterval(async () => {
    if (!running) return;
    try {
      const raw = await page.evaluate(() => {
        const evts =
          (window as unknown as { __canaryRecordedEvents?: unknown[] }).__canaryRecordedEvents ??
          [];
        (window as unknown as { __canaryRecordedEvents: unknown[] }).__canaryRecordedEvents = [];
        return evts;
      });

      if (!Array.isArray(raw) || raw.length === 0) return;

      for (const evt of raw as RecordingEvent[]) {
        // Enrich click events with element info and periodic snapshots
        if (evt.type === 'click') {
          const clickEvt = evt as RecordingClickEvent;
          try {
            const info = await captureElementAtPoint(page, clickEvt.x, clickEvt.y);
            if (info) {
              clickEvt.elementInfo = info as unknown as Record<string, unknown>;
            }
          } catch {
            // Element may have disappeared
          }

          // Snapshot every click for full page context
          try {
            const snapshot = await page.ariaSnapshot({ mode: 'ai' });
            clickEvt.snapshot = snapshot;
          } catch {
            // Snapshot may fail during navigation
          }
        }

        // Enrich navigation events with snapshot
        if (evt.type === 'navigation') {
          try {
            const snapshot = await page.ariaSnapshot({ mode: 'ai' });
            (evt as { snapshot?: string }).snapshot = snapshot;
          } catch {
            // Snapshot may fail during navigation
          }
        }

        events.push(evt);
        const label =
          evt.type === 'click'
            ? `click (${(evt as RecordingClickEvent).tagName})`
            : evt.type === 'input'
              ? `input`
              : evt.type === 'change'
                ? `change (${(evt as { tagName: string }).tagName})`
                : evt.type === 'navigation'
                  ? `navigation → ${(evt as { url: string }).url}`
                  : evt.type === 'initial-url'
                    ? `initial-url → ${(evt as { url: string }).url}`
                    : `keydown: ${(evt as { key: string }).key}`;
        process.stdout.write(`  [${events.length}] ${label}\n`);
      }
    } catch {
      // Page may have been closed
    }
  }, 500);

  // 10. Handle Ctrl+C
  const cleanup = async () => {
    if (!running) return;
    running = false;
    clearInterval(pollInterval);

    console.log(`\nRecording stopped. ${events.length} events captured.`);

    // Save video
    console.log('Saving video...');
    const videoResult = await client.saveVideo();
    await client.disconnect();

    // Clean up temp storage state
    if (storageStatePath) {
      await fs.unlink(storageStatePath).catch(() => {});
    }

    // Prepare output directory
    const outDir = outputDir ?? path.join(getCanaryTmpDir(), `canary-recording-${Date.now()}`);
    await fs.mkdir(outDir, { recursive: true });

    // Write events JSONL
    const eventsPath = path.join(outDir, 'events.jsonl');
    const lines = events.map((e) => JSON.stringify(e)).join('\n');
    await fs.writeFile(eventsPath, lines, 'utf-8');
    console.log(`Events saved: ${eventsPath}`);

    // Copy video if available
    let videoPath: string | undefined;
    if (videoResult?.videoPath) {
      videoPath = path.join(outDir, 'video.webm');
      await fs.copyFile(videoResult.videoPath, videoPath);
      console.log(`Video saved: ${videoPath}`);
    }

    // Upload to API
    if (!skipUpload && events.length > 0) {
      console.log('Uploading recording...');
      try {
        const formData = new FormData();
        formData.append('propertyId', propertyId);
        formData.append('credentialId', credential.id);

        const eventsBlob = new Blob([lines], { type: 'application/x-ndjson' });
        formData.append('events', eventsBlob, 'events.jsonl');

        if (videoPath) {
          const videoBuffer = await fs.readFile(videoPath);
          const videoBlob = new Blob([videoBuffer], { type: 'video/webm' });
          formData.append('video', videoBlob, 'video.webm');
        }

        const res = await fetch(`${config.apiUrl}/org/recordings/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${config.token}` },
          body: formData,
        });

        const json = (await res.json()) as {
          ok: boolean;
          recordingId?: string;
          error?: string;
        };

        if (json.ok) {
          console.log(`Recording uploaded. ID: ${json.recordingId}`);
        } else {
          console.error(`Upload failed: ${json.error ?? 'Unknown error'}`);
        }
      } catch (err) {
        console.error('Upload failed:', err instanceof Error ? err.message : err);
      }
    }

    console.log(`\nOutput directory: ${outDir}`);
    process.exit(0);
  };

  process.on('SIGINT', () => void cleanup());
  process.on('SIGTERM', () => void cleanup());

  // Also stop if the page is closed
  page.on('close', () => void cleanup());
}
